/**
 * Account detail — Publisher / Admin / Super Creator.
 *
 * Ports the publisher dashboard's views/user/info:
 *   - account info + platform KPIs
 *   - lifecycle actions (approve / reject / set-primary)
 *   - inline edit (role / status / name / company / payout currency)
 *   - super-creator's assigned publishers
 *   - publisher financial enrichment (balance / apps / withdrawals),
 *     reusing the Customer Service detail endpoint rather than
 *     re-deriving it.
 */
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle2, LogIn, Pencil, ShieldCheck, Star, XCircle } from 'lucide-react';
import {
  AccountStatus,
  UserDetail,
  UserRole
} from '@/api/adminUsers';
import {
  useApproveUser,
  useRejectUser,
  useSetPrimaryUser,
  useSuperCreatorPublishers,
  useUpdateUser,
  useUser
} from '@/hooks/useAdminUsers';
import { useSupportPublisher } from '@/hooks/useAdminSupport';
import { loginAs, setKycTier } from '@/api/adminSupport';
import {
  ActionButton,
  BackButton,
  Badge,
  Card,
  DetailCell,
  SectionLabel,
  Select,
  Skeleton,
  StatCard
} from '@/components/ui/atoms';
import { fmtDate, fmtDateTime, fmtDuration, fmtNum, formatUsd } from '@/lib/format';

const ROLE_OPTIONS: Array<{ value: UserRole; label: string }> = [
  { value: 'publisher', label: 'Publisher' },
  { value: 'admin', label: 'Admin' },
  { value: 'super_publisher', label: 'Super Creator' },
  { value: 'shareholder', label: 'Shareholder' }
];

const STATUS_OPTIONS: Array<{ value: AccountStatus; label: string }> = [
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'rejected', label: 'Rejected' }
];

const INPUT_CLS =
  'w-full rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-1.5 text-sm text-nurev-text placeholder:text-nurev-muted focus:border-amber-500/50 focus:outline-none';

function pct(rate?: number): string | undefined {
  if (rate === undefined || rate === null) return undefined;
  return `${(rate * 100).toFixed(1)}% of platform`;
}

/** Inline editor for the mutable account fields. Owns its own draft state. */
function AccountEditor({
  user,
  onClose
}: {
  user: UserDetail;
  onClose: () => void;
}) {
  const update = useUpdateUser();
  const [name, setName] = useState(user.name ?? '');
  const [companyName, setCompanyName] = useState(user.companyName ?? '');
  const [role, setRole] = useState<UserRole>(user.role ?? 'publisher');
  const [status, setStatus] = useState<AccountStatus>(user.status ?? 'active');
  const [payoutCurrency, setPayoutCurrency] = useState(user.payoutCurrency ?? '');

  function save() {
    update.mutate(
      {
        id: user._id,
        patch: {
          name: name.trim(),
          companyName: companyName.trim(),
          role,
          status,
          payoutCurrency: payoutCurrency.trim().toUpperCase() || undefined
        }
      },
      { onSuccess: () => onClose() }
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <label className="block">
        <span className="mb-1 block text-[10px] uppercase text-nurev-muted">Name</span>
        <input className={INPUT_CLS} value={name} onChange={e => setName(e.target.value)} />
      </label>
      <label className="block">
        <span className="mb-1 block text-[10px] uppercase text-nurev-muted">Company</span>
        <input
          className={INPUT_CLS}
          value={companyName}
          onChange={e => setCompanyName(e.target.value)}
        />
      </label>
      <label className="block">
        <span className="mb-1 block text-[10px] uppercase text-nurev-muted">
          Payout Currency
        </span>
        <input
          className={`${INPUT_CLS} uppercase`}
          value={payoutCurrency}
          placeholder="USD"
          onChange={e => setPayoutCurrency(e.target.value)}
        />
      </label>
      <div className="block">
        <span className="mb-1 block text-[10px] uppercase text-nurev-muted">Role</span>
        <Select
          className="w-full"
          value={role}
          onChange={v => setRole(v)}
          options={ROLE_OPTIONS}
        />
      </div>
      <div className="block">
        <span className="mb-1 block text-[10px] uppercase text-nurev-muted">Status</span>
        <Select
          className="w-full"
          value={status}
          onChange={v => setStatus(v)}
          options={STATUS_OPTIONS}
        />
      </div>
      <div className="flex items-end gap-2">
        <ActionButton variant="primary" size="sm" disabled={update.isPending} onClick={save}>
          {update.isPending ? 'Saving…' : 'Save'}
        </ActionButton>
        <ActionButton
          variant="neutral"
          size="sm"
          disabled={update.isPending}
          onClick={onClose}
        >
          Cancel
        </ActionButton>
      </div>
    </div>
  );
}

/** Publishers assigned to a super-creator (the legacy "assigns" sub-view). */
function AssignedPublishers({ id }: { id: string }) {
  const navigate = useNavigate();
  const { data, isLoading } = useSuperCreatorPublishers(id, true);

  return (
    <Card>
      <SectionLabel>Assigned Publishers</SectionLabel>
      {isLoading ? (
        <Skeleton className="h-16 w-full" />
      ) : !data || data.length === 0 ? (
        <p className="py-4 text-center text-sm text-nurev-muted">
          No publishers assigned to this super creator.
        </p>
      ) : (
        <div className="space-y-1.5">
          {data.map(p => (
            <button
              key={p._id}
              onClick={() => navigate(`/publishers/${p._id}`)}
              className="flex w-full items-center justify-between rounded-lg px-2 py-2 text-left text-sm transition hover:bg-white/[0.03]"
            >
              <span className="text-nurev-text">{p.name || p.email}</span>
              <span className="flex items-center gap-3">
                <span className="text-nurev-muted">{p.email}</span>
                <Badge value={p.status} />
              </span>
            </button>
          ))}
        </div>
      )}
    </Card>
  );
}

export function CreatorDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: user, isLoading, error } = useUser(id);
  const [editing, setEditing] = useState(false);

  const approve = useApproveUser();
  const reject = useRejectUser();
  const setPrimary = useSetPrimaryUser();
  const busy = approve.isPending || reject.isPending || setPrimary.isPending;
  const [actingAs, setActingAs] = useState(false);
  const [tierBusy, setTierBusy] = useState(false);
  const [kycTier, setKycTierState] = useState<string | undefined>(undefined);
  const currentTier = kycTier ?? user?.kycTier;

  // Open this creator's own dashboard in a new tab. The tab is opened
  // synchronously (before the request) so popup blockers allow it; its URL
  // is set once /login-as returns. The token rides in the fragment.
  const openAsCreator = async () => {
    if (!user) return;
    if (
      !window.confirm(
        `Log in as ${user.email}? A new tab opens their dashboard for 2 hours. Everything you do there is recorded as that account and in the audit log.`
      )
    )
      return;
    const tab = window.open('about:blank', '_blank');
    setActingAs(true);
    try {
      const { token } = await loginAs(user._id);
      const url = `https://app.nurev.io/impersonate#token=${encodeURIComponent(token)}`;
      if (tab) tab.location.href = url;
      else window.location.href = url;
    } catch (e: any) {
      tab?.close();
      window.alert(e?.response?.data?.errors?.msg || e?.response?.data?.error || 'Could not log in as this account.');
    } finally {
      setActingAs(false);
    }
  };

  // Manual KYC Tier 2 — unlocks Windows/Mac installer builds while Sumsub
  // isn't live. A reason is required and recorded.
  const grantTier2 = async () => {
    if (!user) return;
    const reason = window.prompt(
      `Set ${user.email} to Tier 2 (full verification) by hand?\n\nReason (recorded, at least 10 characters):`
    );
    if (reason === null) return;
    if (reason.trim().length < 10) {
      window.alert('The reason needs at least 10 characters.');
      return;
    }
    setTierBusy(true);
    try {
      const r = await setKycTier(user._id, 'tier2_full_verified', reason.trim());
      setKycTierState(r.kycTier);
    } catch (e: any) {
      window.alert(e?.response?.data?.error || e?.response?.data?.errors?.msg || 'Could not change the tier.');
    } finally {
      setTierBusy(false);
    }
  };

  // Financial/app enrichment — publisher-only; non-fatal for other roles.
  const isPublisher = user?.role === 'publisher';
  const { data: support } = useSupportPublisher(isPublisher ? id ?? null : null);

  if (error) {
    return (
      <div className="space-y-4">
        <BackButton onClick={() => navigate(-1)} />
        <Card className="py-10 text-center">
          <AlertCircle className="mx-auto mb-2 h-6 w-6 text-red-400" />
          <p className="text-sm text-nurev-muted">Couldn't load this account.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <BackButton onClick={() => navigate(-1)} />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-nurev-text">
              {isLoading ? <Skeleton className="h-6 w-48" /> : user?.name || user?.email}
            </h1>
            {user?.isPrimary && (
              <span className="rounded bg-amber-500/15 px-2 py-0.5 text-[10px] font-medium text-amber-300">
                primary
              </span>
            )}
          </div>
          {!isLoading && (
            <p className="mt-1 text-sm capitalize text-nurev-muted">
              {(user?.role || '').replace('_', ' ')} · <Badge value={user?.status} />
              {isPublisher && currentTier && (
                <span className="ml-2 normal-case">
                  · KYC: {currentTier === 'tier2_full_verified' ? 'Tier 2' : currentTier === 'tier1_lite_verified' ? 'Tier 1' : currentTier}
                </span>
              )}
            </p>
          )}
        </div>

        {/* Lifecycle + edit actions */}
        {!isLoading && user && !editing && (
          <div className="flex flex-wrap gap-2">
            {user.status === 'pending' && (
              <ActionButton
                variant="positive"
                size="sm"
                disabled={busy}
                onClick={() => approve.mutate(user._id)}
              >
                <CheckCircle2 className="mr-1 inline h-3.5 w-3.5" /> Approve
              </ActionButton>
            )}
            {user.status !== 'rejected' && (
              <ActionButton
                variant="destructive"
                size="sm"
                disabled={busy}
                onClick={() => {
                  if (
                    window.confirm(
                      `Reject ${user.name || user.email}? They lose access until re-approved.`
                    )
                  )
                    reject.mutate(user._id);
                }}
              >
                <XCircle className="mr-1 inline h-3.5 w-3.5" /> Reject
              </ActionButton>
            )}
            {!user.isPrimary && (
              <ActionButton
                variant="neutral"
                size="sm"
                disabled={busy}
                onClick={() => setPrimary.mutate(user._id)}
              >
                <Star className="mr-1 inline h-3.5 w-3.5" /> Set Primary
              </ActionButton>
            )}
            {user.role !== 'admin' && (
              <ActionButton variant="neutral" size="sm" disabled={actingAs} onClick={openAsCreator}>
                <LogIn className="mr-1 inline h-3.5 w-3.5" /> {actingAs ? 'Opening…' : 'Log in as creator'}
              </ActionButton>
            )}
            {isPublisher && currentTier !== 'tier2_full_verified' && currentTier !== 'blocked' && (
              <ActionButton variant="neutral" size="sm" disabled={tierBusy} onClick={grantTier2}>
                <ShieldCheck className="mr-1 inline h-3.5 w-3.5" /> {tierBusy ? 'Saving…' : 'Set Tier 2 (manual)'}
              </ActionButton>
            )}
            <ActionButton variant="info" size="sm" onClick={() => setEditing(true)}>
              <Pencil className="mr-1 inline h-3.5 w-3.5" /> Edit
            </ActionButton>
          </div>
        )}
      </div>

      {/* Platform KPIs */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label="Installs"
          value={fmtNum(user?.installs)}
          sub={pct(user?.installsRate)}
          loading={isLoading}
        />
        <StatCard
          label="Live Now"
          value={fmtNum(user?.live)}
          sub={pct(user?.liveRate)}
          color="text-emerald-300"
          loading={isLoading}
        />
        <StatCard
          label="Live Time"
          value={fmtDuration(user?.liveTime)}
          sub={pct(user?.liveTimeRate)}
          loading={isLoading}
        />
        <StatCard label="Earnings" value={fmtNum(user?.earnings)} loading={isLoading} />
      </div>

      {/* Account info / editor */}
      <Card>
        <SectionLabel>Account</SectionLabel>
        {isLoading || !user ? (
          <Skeleton className="h-24 w-full" />
        ) : editing ? (
          <AccountEditor user={user} onClose={() => setEditing(false)} />
        ) : (
          <div className="grid grid-cols-2 gap-x-6 gap-y-4 lg:grid-cols-3">
            <DetailCell label="Email">{user.email}</DetailCell>
            <DetailCell label="Company">{user.companyName || '—'}</DetailCell>
            <DetailCell label="Phone">{user.phone || '—'}</DetailCell>
            <DetailCell label="Role" capitalize>
              {(user.role || '—').replace('_', ' ')}
            </DetailCell>
            <DetailCell label="Status">
              <Badge value={user.status} />
            </DetailCell>
            <DetailCell label="Publisher Key" mono>
              {user.publisherKey || '—'}
            </DetailCell>
            <DetailCell label="Payout Currency" upper>
              {user.payoutCurrency || '—'}
            </DetailCell>
            <DetailCell label="Joined">{fmtDate(user.createdAt)}</DetailCell>
          </div>
        )}
      </Card>

      {/* Super-creator's assigned publishers */}
      {user?.role === 'super_publisher' && id && <AssignedPublishers id={id} />}

      {/* Publisher financial enrichment (from /admin-support/publisher/:id) */}
      {support && (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatCard label="Balance" value={formatUsd(support.balance)} />
            <StatCard
              label="Devices"
              value={fmtNum(support.devices?.total)}
              sub={`${support.devices?.installed || 0} installed`}
            />
            <StatCard
              label="Active Sessions"
              value={fmtNum(support.activeSessions)}
              color="text-emerald-300"
            />
            <StatCard label="Apps" value={fmtNum(support.apps?.length)} />
          </div>

          {support.apps && support.apps.length > 0 && (
            <Card>
              <SectionLabel>Apps</SectionLabel>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-nurev-border text-left text-[10px] uppercase tracking-wider text-nurev-muted">
                      <th className="py-2 pr-3 font-medium">Name</th>
                      <th className="py-2 pr-3 font-medium">App Key</th>
                      <th className="py-2 pr-3 text-right font-medium">Installs</th>
                      <th className="py-2 pr-3 text-right font-medium">Live</th>
                      <th className="py-2 pr-3 text-right font-medium">Earnings</th>
                    </tr>
                  </thead>
                  <tbody>
                    {support.apps.map(a => (
                      <tr key={a._id} className="border-b border-nurev-border/50">
                        <td className="py-2 pr-3 text-nurev-text">{a.name || '—'}</td>
                        <td className="py-2 pr-3 font-mono text-xs text-nurev-muted">
                          {a.appKey}
                        </td>
                        <td className="py-2 pr-3 text-right text-nurev-text">
                          {fmtNum(a.installs)}
                        </td>
                        <td className="py-2 pr-3 text-right text-emerald-300">
                          {fmtNum(a.live)}
                        </td>
                        <td className="py-2 pr-3 text-right text-nurev-text">
                          {fmtNum(a.earnings)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {support.recentWithdrawals && support.recentWithdrawals.length > 0 && (
            <Card>
              <SectionLabel>Recent Withdrawals</SectionLabel>
              <div className="space-y-2">
                {support.recentWithdrawals.map(w => (
                  <div
                    key={w._id}
                    className="flex items-center justify-between border-b border-nurev-border/50 pb-2 text-sm last:border-0"
                  >
                    <span className="text-nurev-muted">{fmtDateTime(w.createdAt)}</span>
                    <span className="text-nurev-text">
                      {fmtNum(w.fromAmount)} {w.fromCurrency} → {fmtNum(w.toAmount)}{' '}
                      {w.outCurrency}
                    </span>
                    <Badge value={w.status} />
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
